import * as React from 'react';
import {Platform, StatusBar} from 'react-native';

import FooterCloseButton from '../shared/viewer/FooterCloseButton';
import GoogleAds from '../shared/viewer/GoogleAds';

import {Params as EpisodeViewerErrorLogCreateParams} from '../../actions/episode_viewer_error_logs/create';

import NavigationProp from '../../navigators/NavigationProp';
import {PreviewSceneViewerRouteProp} from '../../navigators/RouteProps';

import StorageViewerSettingsRepository from '../../../data/repositories/StorageViewerSettingsRepository';

import EpisodeViewerErrorLog from '../../../domain/entities/EpisodeViewerErrorLog';

import {WriterPreviewSceneViewer} from '../../../vendor/react-native-tapnovel-viewer';

const orientation = 'vertical';

export interface Params {
  params: WriterPreviewSceneViewer.Params;
  episodeId: number;
  sceneId?: number;
  withNextScenes?: boolean;
}

export interface StateProps {
  navigation: NavigationProp;
  route: PreviewSceneViewerRouteProp;
}

export interface DispatchProps {
  createEpisodeViewerErrorLog: (
    params: EpisodeViewerErrorLogCreateParams,
  ) => Promise<EpisodeViewerErrorLog>;
}

interface Props extends StateProps, DispatchProps {}

interface State {
  visibleProgressBar: boolean;
  textSpeed: 'slow' | 'normal' | 'fast' | 'no_effect';
  enabledSound: boolean;
  autoPlaySpeed: 0 | 1 | 1.5 | 2;
}

export default class Show extends React.PureComponent<Props, State> {
  private viewerSettingsRepository: StorageViewerSettingsRepository =
    new StorageViewerSettingsRepository();

  constructor(props: Props) {
    super(props);
    this.state = {
      visibleProgressBar: true,
      textSpeed: 'no_effect',
      enabledSound: true,
      autoPlaySpeed: 0,
    };
  }

  public async componentDidMount(): Promise<void> {
    const {navigation, route} = this.props;
    const {params, episodeId, sceneId} = route.params;
    if (typeof params === 'string') {
      setTimeout(() => {
        navigation.goBack();
        if (sceneId) {
          navigation.navigate('EditScene', {sceneId: Number(sceneId)});
        } else if (episodeId) {
          navigation.navigate('NewScene', {episodeId: Number(episodeId)});
        }
      }, 300);
      return;
    }
    StatusBar.setHidden(true);
    const viewerSetting = await this.viewerSettingsRepository.find();
    const visibleProgressBar = viewerSetting.visibleProgressBar;
    const textSpeed = viewerSetting.textSpeed;
    const enabledSound =
      viewerSetting.enabledSound === undefined
        ? true
        : viewerSetting.enabledSound;
    const autoPlaySpeed =
      viewerSetting.autoPlaySpeed === undefined
        ? 1
        : viewerSetting.autoPlaySpeed;
    this.setState({
      visibleProgressBar,
      textSpeed,
      enabledSound,
      autoPlaySpeed,
    });
  }

  public componentWillUnmount(): void {
    StatusBar.setHidden(false);
  }

  public render(): React.ReactNode {
    const {route} = this.props;
    const {visibleProgressBar, textSpeed, enabledSound, autoPlaySpeed} =
      this.state;
    const {params, withNextScenes} = route.params;
    if (typeof params === 'string') {
      return null;
    }
    return (
      <WriterPreviewSceneViewer.default
        params={params}
        withNextScenes={withNextScenes}
        visibleProgressBar={visibleProgressBar}
        textSpeed={textSpeed}
        enabledSound={enabledSound}
        autoPlaySpeed={autoPlaySpeed}
        orientation={orientation}
        maker={true}
        renderDownloadProgressModalTop={this.renderDownloadProgressModalTop}
        renderFooter={this.renderFooter}
        onLoadFail={this.handleLoadFail}
        onRequestClose={this.handleRequestClose}
        onVisibleProgressBarChange={this.handleVisibleProgressBarChange}
        onTextSpeedChange={this.handleTextSpeedChange}
        onAutoPlaySpeedChange={this.handleAutoPlaySpeedChange}
        onEnabledSoundChange={this.handleEnabledSoundChange}
      />
    );
  }

  private renderDownloadProgressModalTop = (width: number): React.ReactNode => {
    return null;
  };

  private renderFooter = (): React.ReactNode => {
    return <FooterCloseButton onPressFinish={this.handleRequestClose} />;
  };

  private handleLoadFail = (error: any) => {
    const {route, createEpisodeViewerErrorLog} = this.props;
    const episodeId = Number(route.params.episodeId);
    const sceneId = Number(route.params.sceneId);
    createEpisodeViewerErrorLog({
      episodeId,
      sceneId,
      errorUrl: `tapnovel-maker://scenes/previewer?episodeId=${episodeId}${
        sceneId ? `&sceneId=${sceneId}` : ''
      }`,
      errorInfo: {
        name: error.name,
        stack: error.stack,
        message: error.message,
      },
      platform: Platform.select({
        ios: 'ios',
        android: 'android',
        default: 'web',
      }),
    });
  };

  private handleRequestClose = () => {
    const {navigation} = this.props;
    navigation.goBack();
  };

  private handleVisibleProgressBarChange = (visibleProgressBar: boolean) => {
    this.setState({visibleProgressBar});
    this.viewerSettingsRepository.update({visibleProgressBar});
  };

  private handleTextSpeedChange = (
    textSpeed: 'slow' | 'normal' | 'fast' | 'no_effect',
  ) => {
    this.setState({textSpeed});
    this.viewerSettingsRepository.update({textSpeed});
  };

  private handleAutoPlaySpeedChange = (autoPlaySpeed: 0 | 1 | 1.5 | 2) => {
    this.setState({autoPlaySpeed});
    this.viewerSettingsRepository.update({autoPlaySpeed});
  };

  private handleEnabledSoundChange = (enabledSound: boolean) => {
    this.setState({enabledSound});
    this.viewerSettingsRepository.update({enabledSound});
  };
}
